import { useState, useRef } from 'react'
import { Plus } from 'lucide-react'
import type { RequirementType } from '@ispot/shared'
import { REQUIREMENT_TYPE_BY_DEPTH } from './constants'
import { gridTemplate } from './grid'

interface QuickAddRowProps {
  parentId: string | null
  depth: number
  visibleColumns: Record<string, boolean>
  onCreate: (data: { name: string; type: RequirementType; parentId?: string }) => Promise<unknown>
}

export function QuickAddRow({ parentId, depth, visibleColumns, onCreate }: QuickAddRowProps) {
  const [isActive, setIsActive] = useState(false)
  const [value, setValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const indent = depth * 20

  async function handleSubmit() {
    const name = value.trim()
    if (!name || isSaving) return
    setIsSaving(true)
    try {
      await onCreate({
        name,
        type: REQUIREMENT_TYPE_BY_DEPTH[depth] ?? 'story',
        parentId: parentId ?? undefined,
      })
      setValue('')
      inputRef.current?.focus()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: gridTemplate(visibleColumns),
        alignItems: 'center',
        minHeight: 36,
        borderBottom: '1px dashed var(--color-warm-200)',
      }}
    >
      {/* checkbox column */}
      <span aria-hidden />
      {/* expand column */}
      <span aria-hidden />
      {/* name column — spans remainder effectively via child grid-column override */}
      <div className="flex items-center" style={{ paddingLeft: indent + 32, gridColumn: `3 / -1` }}>
        {isActive ? (
          <input
            ref={inputRef}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') { setValue(''); setIsActive(false) } }}
            onBlur={() => { if (!value.trim()) setIsActive(false) }}
            placeholder="Type a name and press Enter..."
            autoFocus
            className="flex-1 bg-transparent outline-none text-[13px] py-1"
            style={{ color: 'var(--color-text)', fontFamily: 'var(--font-sans)' }}
          />
        ) : (
          <button
            className="inline-flex items-center gap-1.5 text-sm py-1.5 cursor-pointer bg-transparent border-0"
            style={{ color: 'var(--color-fg-muted)', fontFamily: 'var(--font-body)' }}
            onClick={() => setIsActive(true)}
          >
            <Plus className="h-4 w-4" />
            Add {depth === 0 ? 'epic' : 'story'}…
          </button>
        )}
      </div>
    </div>
  )
}
